import React, { useState, useEffect } from 'react';
import '@/App.css';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import axios from 'axios';
import Login from './pages/Login';
import Register from './pages/Register';
import Dashboard from './pages/Dashboard';
import AdminDashboard from './pages/AdminDashboard';
import HomeCommandCenter from './pages/HomeCommandCenter';
import AskStewieLanding from './pages/AskStewieLanding';
import LibraryPage from './pages/LibraryPage';
import ProfilePage from './pages/ProfilePage';
import AdminAnalytics from './pages/AdminAnalytics';
import WorkerVoicePage from './pages/WorkerVoicePage';
import AdminIntelligencePage from './pages/AdminIntelligencePage';
import AIAssistant from './pages/AIAssistant';
import SafetyRules from './pages/SafetyRules';
import UserGuide from './pages/UserGuide';
import AdminAPIKeys from './pages/AdminAPIKeys';
import KnowledgeCheck from './pages/KnowledgeCheck';
import AskStewieAnalytics from './pages/AskStewieAnalytics';
import ReportIssuePage from './pages/ReportIssuePage';
import JobBriefingPage from './pages/JobBriefingPage';
import JobBriefingLandingPage from './pages/JobBriefingLandingPage';
import JobBriefV2Home from './pages/JobBriefV2Home';
import JobBriefV2Editor from './pages/JobBriefV2Editor';
import { isJobBriefV2Enabled } from './utils/jobBriefFeatureFlag';
import AdminAnalyticsNew from './pages/AdminAnalyticsNew';
import AdminAnalyticsDashboard from './pages/AdminAnalyticsDashboard';
import AskStewieApp from './pages/AskStewieApp';
import FactFinderPageLegacy from './pages/FactFinderPage';
import FactFinderFormPage from './pages/FactFinderPage.jsx';
import DraftsDashboard from './pages/DraftsDashboard';
import FactFinderV2Page from './pages/FactFinderV2Page';
import StatementBuilderPage from './pages/StatementBuilderPage';
import GrievanceBuilderPage from './pages/GrievanceBuilderPage';
import TagReviewPage from './pages/TagReviewPage';
import GrievanceCaseIntelligencePage from './pages/GrievanceCaseIntelligencePage';
import ConductPredictorPage from './pages/ConductPredictorPage';
import CaseLockerPage from './pages/CaseLockerPage';
import DocumentGovernancePage from './pages/DocumentGovernancePage';
import SourceStatusPage from './pages/SourceStatusPage';
import ReviewQueuePage from './pages/ReviewQueuePage';
import PatternIntelligencePage from './pages/PatternIntelligencePage';
import VocabularyAdminPage from './pages/VocabularyAdminPage';
import RetrievalComparePage from './pages/RetrievalComparePage';
import FollowUpAnalyticsPage from './pages/FollowUpAnalyticsPage';
import CaseAnalyticsPage from './pages/CaseAnalyticsPage';
import AppShell from './layouts/AppShell';
import { ModeProvider } from './contexts/ModeContext';
import { ThemeProvider } from './contexts/ThemeContext';
import ModeSwitchModal from './components/ModeSwitchModal';
import FeatureRoute from './components/routes/FeatureRoute';
import { canSeeKnowledgeCheck, canSeeTrainingMode, canSeeReference } from './utils/featureAccess';
import { useAnalyticsSession } from './hooks/useAnalyticsSession';
import { asSmokeTest } from "@/modules/ask-stewie";

// Module smoke tests (development only)
if (process.env.NODE_ENV === "development") {
  asSmokeTest()
    .then(() => console.log("✓ AskStewie smoke test passed"))
    .catch((e) => console.error("✗ AskStewie smoke test failed", e));
}

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// Axios interceptor for auth
axios.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useAnalyticsSession(user);

  const checkAuth = async () => {
    const token = localStorage.getItem('token');
    if (token) {
      try {
        const response = await axios.get(`${API}/auth/me`);
        setUser(response.data);
      } catch (error) {
        localStorage.removeItem('token');
      }
    }
    setLoading(false);
  };

  useEffect(() => {
    checkAuth();
  }, []);

  const handleLogin = (userData, token) => {
    localStorage.setItem('token', token);
    setUser(userData);
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    setUser(null);
  };

  if (loading) {
    return (
      <div className="loading-screen">
        <div className="loader"></div>
      </div>
    );
  }

  return (
    <ThemeProvider>
    <ModeProvider>
      <BrowserRouter>
        <ModeSwitchModal />
        <Routes>
        <Route
          path="/login"
          element={!user ? <Login onLogin={handleLogin} /> : <Navigate to="/" />}
        />
        <Route
          path="/register"
          element={!user ? <Register onLogin={handleLogin} /> : <Navigate to="/" />}
        />
        <Route
          path="/"
          element={
            user ? (
              <AskStewieLanding user={user} onLogout={handleLogout} />
            ) : (
              <Navigate to="/login" />
            )
          }
        />
        <Route
          path="/command-center"
          element={
            user ? (
              <HomeCommandCenter user={user} onLogout={handleLogout} />
            ) : (
              <Navigate to="/login" />
            )
          }
        />
        <Route
          path="/library"
          element={
            user ? (
              <LibraryPage user={user} />
            ) : (
              <Navigate to="/login" />
            )
          }
        />
        <Route
          path="/profile"
          element={
            user ? (
              <ProfilePage user={user} onLogout={handleLogout} />
            ) : (
              <Navigate to="/login" />
            )
          }
        />
        <Route
          path="/ai-assistant"
          element={
            user ? (
              <AppShell user={user} onLogout={handleLogout}>
                <AIAssistant user={user} onLogout={handleLogout} />
              </AppShell>
            ) : (
              <Navigate to="/login" />
            )
          }
        />
        <Route
          path="/safety-rules"
          element={
            user ? (
              <AppShell user={user} onLogout={handleLogout}>
                <SafetyRules user={user} onLogout={handleLogout} />
              </AppShell>
            ) : (
              <Navigate to="/login" />
            )
          }
        />
        <Route
          path="/user-guide"
          element={
            user ? (
              <AppShell user={user} onLogout={handleLogout}>
                <UserGuide user={user} onLogout={handleLogout} />
              </AppShell>
            ) : (
              <Navigate to="/login" />
            )
          }
        />
        <Route
          path="/training/knowledge-check"
          element={
            user ? (
              <FeatureRoute allowed={canSeeKnowledgeCheck(user)}>
                <KnowledgeCheck user={user} />
              </FeatureRoute>
            ) : (
              <Navigate to="/login" />
            )
          }
        />
        <Route
          path="/admin/api-keys"
          element={
            user && user.is_admin ? (
              <AppShell user={user} onLogout={handleLogout}>
                <AdminAPIKeys user={user} onLogout={handleLogout} />
              </AppShell>
            ) : (
              <Navigate to="/login" />
            )
          }
        />
        <Route
          path="/admin/analytics"
          element={
            user && user.is_admin ? (
              <AppShell user={user}>
                <AdminAnalytics user={user} />
              </AppShell>
            ) : (
              <Navigate to="/login" />
            )
          }
        />
        <Route
          path="/ask-stewie"
          element={
            user ? (
              <AppShell user={user}>
                <AskStewieAnalytics user={user} />
              </AppShell>
            ) : (
              <Navigate to="/login" />
            )
          }
        />
        <Route
          path="/report-issue"
          element={
            user ? (
              <AppShell user={user}><ReportIssuePage user={user} /></AppShell>
            ) : (
              <Navigate to="/login" />
            )
          }
        />
        <Route
          path="/job-briefing"
          element={
            user ? (
              isJobBriefV2Enabled()
                ? <AppShell user={user}><JobBriefV2Home user={user} onLogout={handleLogout} /></AppShell>
                : <AppShell user={user}><JobBriefingLandingPage user={user} /></AppShell>
            ) : (
              <Navigate to="/login" />
            )
          }
        />
        <Route
          path="/job-briefing/v2"
          element={
            user ? (
              <AppShell user={user}><JobBriefV2Home user={user} onLogout={handleLogout} /></AppShell>
            ) : (
              <Navigate to="/login" />
            )
          }
        />
        <Route
          path="/job-briefing/v2/:id"
          element={
            user ? (
              <AppShell user={user}><JobBriefV2Editor user={user} onLogout={handleLogout} /></AppShell>
            ) : (
              <Navigate to="/login" />
            )
          }
        />
        <Route
          path="/job-briefing/new"
          element={
            user ? (
              <AppShell user={user}><JobBriefingPage user={user} /></AppShell>
            ) : (
              <Navigate to="/login" />
            )
          }
        />
        <Route
          path="/fact-finder"
          element={
            user ? (
              <AppShell user={user}><FactFinderV2Page user={user} /></AppShell>
            ) : (
              <Navigate to="/login" />
            )
          }
        />
        <Route
          path="/fact-finder-old"
          element={
            user ? (
              <AppShell user={user}><FactFinderPageLegacy user={user} /></AppShell>
            ) : (<Navigate to="/login" />)
          }
        />
        <Route
          path="/fact-finder/form"
          element={
            user ? (
              <AppShell user={user}><FactFinderFormPage user={user} /></AppShell>
            ) : (<Navigate to="/login" />)
          }
        />
        <Route
          path="/dashboard/drafts"
          element={
            user ? (
              <AppShell user={user}><DraftsDashboard /></AppShell>
            ) : (<Navigate to="/login" />)
          }
        />
        <Route
          path="/statement-builder"
          element={
            user ? (
              <AppShell user={user}><StatementBuilderPage user={user} /></AppShell>
            ) : (<Navigate to="/login" />)
          }
        />
        <Route
          path="/admin/analytics-new"
          element={
            user && user.is_admin ? (
              <AppShell user={user}><AdminAnalyticsNew user={user} /></AppShell>
            ) : (<Navigate to="/login" />)
          }
        />
        <Route
          path="/admin/analytics-dashboard"
          element={
            user && user.is_admin ? (
              <AppShell user={user}><AdminAnalyticsDashboard user={user} /></AppShell>
            ) : (<Navigate to="/login" />)
          }
        />
        <Route
          path="/stewie-app"
          element={<AppShell user={user}><AskStewieApp /></AppShell>}
        />
        <Route
          path="/grievance-builder"
          element={
            user ? (
              <AppShell user={user}><GrievanceBuilderPage user={user} /></AppShell>
            ) : (<Navigate to="/login" />)
          }
        />
        <Route
          path="/grievance-builder/:draftId"
          element={
            user ? (
              <AppShell user={user}><GrievanceBuilderPage user={user} /></AppShell>
            ) : (<Navigate to="/login" />)
          }
        />
        <Route
          path="/admin/grievance-intelligence"
          element={
            user && user.is_admin ? (
              <AppShell user={user}><GrievanceCaseIntelligencePage user={user} /></AppShell>
            ) : (<Navigate to="/login" />)
          }
        />
        <Route
          path="/conduct-predictor"
          element={
            user ? (
              <AppShell user={user}><ConductPredictorPage user={user} /></AppShell>
            ) : (<Navigate to="/login" />)
          }
        />
        <Route
          path="/case-locker"
          element={
            user ? (
              <AppShell user={user}><CaseLockerPage user={user} /></AppShell>
            ) : (<Navigate to="/login" />)
          }
        />
        <Route
          path="/case-locker/:caseId"
          element={
            user ? (
              <AppShell user={user}><CaseLockerPage user={user} /></AppShell>
            ) : (<Navigate to="/login" />)
          }
        />
        <Route
          path="/admin/source-status"
          element={
            user && user.is_admin ? (
              <AppShell user={user}><SourceStatusPage /></AppShell>
            ) : (<Navigate to="/login" />)
          }
        />
        <Route
          path="/admin/document-governance"
          element={
            user && user.is_admin ? (
              <AppShell user={user}><DocumentGovernancePage user={user} /></AppShell>
            ) : (<Navigate to="/login" />)
          }
        />
        <Route
          path="/admin/tag-review"
          element={
            user && user.is_admin ? (
              <AppShell user={user}><TagReviewPage user={user} /></AppShell>
            ) : (<Navigate to="/login" />)
          }
        />
        <Route
          path="/admin/review-queue"
          element={
            user && user.is_admin ? (
              <AppShell user={user}><ReviewQueuePage user={user} /></AppShell>
            ) : (<Navigate to="/login" />)
          }
        />
        <Route
          path="/admin/pattern-intelligence"
          element={
            user && user.is_admin ? (
              <AppShell user={user}><PatternIntelligencePage user={user} /></AppShell>
            ) : (<Navigate to="/login" />)
          }
        />
        <Route
          path="/admin/vocabulary"
          element={
            user && user.is_admin ? (
              <AppShell user={user}><VocabularyAdminPage user={user} /></AppShell>
            ) : (<Navigate to="/login" />)
          }
        />
        <Route
          path="/admin/retrieval-compare"
          element={
            user && user.is_admin ? (
              <AppShell user={user}><RetrievalComparePage user={user} /></AppShell>
            ) : (<Navigate to="/login" />)
          }
        />
        <Route
          path="/admin/follow-up-analytics"
          element={
            user && user.is_admin ? (
              <AppShell user={user}><FollowUpAnalyticsPage user={user} /></AppShell>
            ) : (<Navigate to="/login" />)
          }
        />
        <Route
          path="/admin/case-analytics"
          element={
            user && user.is_admin ? (
              <AppShell user={user}><CaseAnalyticsPage user={user} /></AppShell>
            ) : (<Navigate to="/login" />)
          }
        />
        <Route
          path="/worker-voice"
          element={
            user ? (
              <WorkerVoicePage user={user} />
            ) : (
              <Navigate to="/login" />
            )
          }
        />
        <Route
          path="/admin/intelligence"
          element={
            user && user.is_admin ? (
              <AdminIntelligencePage user={user} />
            ) : (
              <Navigate to="/login" />
            )
          }
        />
        <Route
          path="/admin"
          element={
            user && user.is_admin ? (
              <AppShell user={user} onLogout={handleLogout}><AdminDashboard user={user} onLogout={handleLogout} /></AppShell>
            ) : (<Navigate to="/login" />)
          }
        />
        {/* Catch-all fallback */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </BrowserRouter>
    </ModeProvider>
    </ThemeProvider>
  );
}

export default App;
